import { MAX_CHARACTERS_PER_ACCOUNT, DEFAULT_APPEARANCE, applyCosmetics, type CharacterDTO } from "@ghostland/shared";
import { deleteCharacter } from "../characters/api.js";
import { showCharacterEditor } from "./characterCreate.js";
import { showDiscordBadge } from "./discordBadge.js";
import { attachIdlePreview, type IdlePreview } from "../sprites/idlePreview.js";
import { injectTheme } from "./theme.js";
import { mountGhostLogo } from "./ghostLogo.js";
import { injectBillStyle, cashIcon, bankIcon } from "./pixelBills.js";

const STYLE_ID = "ghostland-charselect-style";

/**
 * Character selection — GhostLand design ideology: solid black, minimal, spacious.
 * Monochrome (black / white / gray) — no decorative gold or fire. The selected
 * character stands dead-centre on the stage in its idle animation; slots are quiet
 * text rows on the right. The staggered "GHOST LAND" wordmark sits up top.
 */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-csel-overlay {
  position: fixed; inset: 0; z-index: 11;
  display: flex; align-items: center; justify-content: center; gap: 56px;
  background: #000; color: #f4f4f4;
}
/* this screen is intentionally PIXEL-styled (overrides the global sans face) */
#gl-csel-overlay, #gl-csel-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }

/* left: the character with its name + coins right beneath it; the whole group is
   centred in the space below the title (top padding clears the wordmark). A soft
   grayscale vignette gives depth — no frame, no torches. */
#gl-csel-stage {
  flex: 0 1 auto; position: relative;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; padding: 56px 40px;
  background: radial-gradient(ellipse 58% 48% at 50% 50%, rgba(255,255,255,.05), transparent 70%);
}
#gl-csel-stage canvas { image-rendering: pixelated; width: 400px; height: 400px; }
/* name + coins sit directly under the character */
#gl-csel-meta { display: flex; flex-direction: column; align-items: center; gap: 6px; }
#gl-csel-meta .pname { font-size: 22px; letter-spacing: 0.1em; text-transform: uppercase; color: #f4f4f4; text-shadow: 0 2px 0 #000; }
#gl-csel-meta .pcoins { font-size: 15px; color: #cfcfcf; display: inline-flex; align-items: center; gap: 18px; }
.gl-amt { display: inline-flex; align-items: center; gap: 6px; }

/* right: the options — middle-aligned with the centred character column, nudged down 10% */
#gl-csel-panel {
  width: 320px; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 24px; padding: 0; transform: translateY(10vh);
}
#gl-csel-title { font-size: 15px; letter-spacing: 0.32em; color: #b8b8b8; text-transform: uppercase; text-align: center; }
#gl-csel-slots { display: flex; flex-direction: column; gap: 10px; width: 100%; }
.gl-csel-slot {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 14px; cursor: pointer; border: 2px solid #262626;
  color: #8a8a8a; font-size: 15px; letter-spacing: 0.04em; background: #0a0a0a;
}
.gl-csel-slot:hover { color: #d8d8d8; border-color: #4a4a4a; }
.gl-csel-slot.selected { color: #f4f4f4; border-color: #f4f4f4; box-shadow: inset 0 0 0 1px #5a5a5a, 0 0 12px rgba(255,255,255,.08); }
.gl-csel-slot .co { font-size: 13px; color: #9a9a9a; display: inline-flex; align-items: center; gap: 12px; }
.gl-csel-slot.selected .co { color: #d8d8d8; }
.gl-csel-slot.empty { color: #3a3a3a; border-style: dashed; background: none; }
.gl-csel-slot.empty:hover { color: #8a8a8a; border-color: #4a4a4a; }

/* pixel-design buttons */
#gl-csel-actions { display: flex; flex-direction: column; gap: 12px; margin-top: 12px; width: 100%; }
.gl-csel-btn {
  background: #000; border: 2px solid #fff; color: #fff; padding: 14px 12px; cursor: pointer;
  text-align: center; font-size: 14px; letter-spacing: 0.16em; text-transform: uppercase;
}
.gl-csel-btn:hover:not(:disabled) { background: #fff; color: #000; }
.gl-csel-btn:disabled { opacity: 0.3; cursor: default; }
.gl-csel-btn.primary { background: #f4f4f4; border-color: #fff; color: #000; }
.gl-csel-btn.primary:hover:not(:disabled) { background: #fff; }
.gl-csel-btn.danger { border-color: #6a3a3a; color: #c07a7a; }
.gl-csel-btn.danger:hover:not(:disabled) { background: var(--gl-error); border-color: var(--gl-error); color: #000; }
#gl-csel-error { min-height: 14px; font-size: 12px; color: var(--gl-error); letter-spacing: 0.04em; text-align: center; }
#gl-csel-logout {
  margin-top: 22px; background: none; border: none; color: #5a5a5a; cursor: pointer;
  text-align: center; padding: 4px 0; font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase;
}
#gl-csel-logout:hover { color: #f4f4f4; background: none; }
`;

function ensureStyle(): void {
  injectTheme();
  injectBillStyle();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function confirmDelete(name: string): Promise<boolean> {
  return new Promise((resolve) => {
    const ok = window.confirm(`Delete "${name}"? Their coins and inventory are lost permanently.`);
    resolve(ok);
  });
}

/**
 * The post-login character selection screen. Resolves with the character the player
 * chooses to play (Play). Create / customize / delete happen in place.
 */
export function showCharacterSelect(initial: CharacterDTO[]): Promise<CharacterDTO | "logout"> {
  ensureStyle();
  showDiscordBadge();

  return new Promise<CharacterDTO | "logout">((resolve) => {
    let chars = [...initial];
    let selectedId: string | null = chars[0]?.characterId ?? null;
    let preview: IdlePreview | null = null;

    const overlay = document.createElement("div");
    overlay.id = "gl-csel-overlay";
    overlay.innerHTML = `
      <div id="gl-csel-stage">
        <canvas id="gl-csel-canvas" width="432" height="432"></canvas>
        <div id="gl-csel-meta">
          <span class="pname"></span>
          <span class="pcoins"></span>
        </div>
      </div>
      <div id="gl-csel-panel">
        <span id="gl-csel-title">Characters</span>
        <div id="gl-csel-slots"></div>
        <div id="gl-csel-actions">
          <button class="gl-csel-btn primary" id="gl-csel-play" type="button">Enter the world</button>
          <button class="gl-csel-btn danger" id="gl-csel-del" type="button">Delete</button>
        </div>
        <div id="gl-csel-error"></div>
        <button class="gl-csel-btn ghost" id="gl-csel-logout" type="button">Log out</button>
      </div>`;
    document.body.appendChild(overlay);

    const logo = mountGhostLogo(overlay);

    const slotsEl = overlay.querySelector<HTMLDivElement>("#gl-csel-slots")!;
    const canvas = overlay.querySelector<HTMLCanvasElement>("#gl-csel-canvas")!;
    const nameEl = overlay.querySelector<HTMLSpanElement>(".pname")!;
    const coinsEl = overlay.querySelector<HTMLSpanElement>(".pcoins")!;
    const playBtn = overlay.querySelector<HTMLButtonElement>("#gl-csel-play")!;
    const delBtn = overlay.querySelector<HTMLButtonElement>("#gl-csel-del")!;
    const logoutBtn = overlay.querySelector<HTMLButtonElement>("#gl-csel-logout")!;
    const errorEl = overlay.querySelector<HTMLDivElement>("#gl-csel-error")!;

    const selected = (): CharacterDTO | undefined => chars.find((c) => c.characterId === selectedId);

    function render(): void {
      errorEl.textContent = "";
      slotsEl.replaceChildren();
      for (let i = 0; i < MAX_CHARACTERS_PER_ACCOUNT; i++) {
        const char = chars.find((c) => c.slot === i);
        const slot = document.createElement("div");
        if (char) {
          slot.className = "gl-csel-slot" + (char.characterId === selectedId ? " selected" : "");
          slot.innerHTML = `<span class="nm">${char.name}</span><span class="co"><span class="gl-amt">${cashIcon()} ${char.balance ?? 0}</span><span class="gl-amt">${bankIcon()} ${char.bankMoney ?? 0}</span></span>`;
          slot.addEventListener("click", () => {
            selectedId = char.characterId ?? null;
            render();
          });
        } else {
          slot.className = "gl-csel-slot empty";
          slot.textContent = "+ new";
          slot.addEventListener("click", () => void onCreate());
        }
        slotsEl.appendChild(slot);
      }

      const sel = selected();
      if (sel) {
        // Preview the DISPLAY appearance: gear with the cosmetic layer overlaid (matching in-game),
        // unless this character has cosmetics toggled off.
        const gear = { ...DEFAULT_APPEARANCE, ...(sel.appearance ?? {}) };
        const appearance = (sel.showCosmetics ?? true)
          ? applyCosmetics(gear, { cosHat: sel.cosHat ?? "", cosTop: sel.cosTop ?? "", cosBottom: sel.cosBottom ?? "", cosShoes: sel.cosShoes ?? "" })
          : gear;
        if (!preview) preview = attachIdlePreview(canvas, appearance);
        else preview.setAppearance(appearance);
        nameEl.textContent = sel.name;
        coinsEl.innerHTML = `<span class="gl-amt">${cashIcon()} ${sel.balance ?? 0}</span><span class="gl-amt">${bankIcon()} ${sel.bankMoney ?? 0}</span>`;
      } else {
        preview?.dispose();
        preview = null;
        canvas.getContext("2d")!.clearRect(0, 0, canvas.width, canvas.height);
        nameEl.textContent = "";
        coinsEl.textContent = "";
      }

      playBtn.disabled = !sel;
      delBtn.disabled = !sel;
    }

    async function onCreate(): Promise<void> {
      const created = await showCharacterEditor({});
      if (!created) return;
      chars.push(created);
      selectedId = created.characterId ?? null;
      render();
    }

    async function onDelete(): Promise<void> {
      const sel = selected();
      if (!sel) return;
      if (!(await confirmDelete(sel.name))) return;
      try {
        await deleteCharacter(sel.characterId!);
        chars = chars.filter((c) => c.characterId !== sel.characterId);
        selectedId = chars[0]?.characterId ?? null;
        render();
      } catch (err) {
        errorEl.textContent = err instanceof Error ? err.message : "Couldn't delete that character.";
      }
    }

    playBtn.addEventListener("click", () => {
      const sel = selected();
      if (!sel) return;
      preview?.dispose();
      logo.dispose();
      overlay.remove();
      resolve(sel);
    });
    delBtn.addEventListener("click", () => void onDelete());
    logoutBtn.addEventListener("click", () => {
      preview?.dispose();
      logo.dispose();
      overlay.remove();
      resolve("logout");
    });

    render();
  });
}
